import { isLinkGuestEmail } from '@werft/domain';
import { useQuery } from '@tanstack/react-query';
import type { BrandingDto } from '@werft/contracts';
import { api } from '../platform/api.js';
import { accentStyle } from '../platform/branding.js';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { clearPersisted } from '../platform/persist.js';
import { clearDocDrafts } from '../features/docs/localDrafts.js';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Home, LogOut, Search, Shield, Sun, SunMoon, UserRound, Building2 } from 'lucide-react';
import { t } from '../i18n/index.js';
import { authClient } from '../platform/auth.js';
import { queryClient, useConfig, useMe } from '../platform/queries.js';
import { syncDisplay, useResolvedTheme } from '../platform/display.js';
import { useOutboxStatus } from '../platform/outbox.js';
import { realtime, useConnectionStatus } from '../platform/realtime.js';
import { outbox } from '../platform/sync.js';
import { hasUnsafe, useProblems } from '../platform/saveStatus.js';
import { CopyButton, DiscardButton } from '../ui/SaveActions.js';
import { Popover } from '../ui/Popover.js';
import { toast } from '../platform/toast.js';
import { Button } from '../ui/ui.js';
import { Toaster } from '../ui/Toaster.js';
import { InboxButton } from '../features/inbox/Inbox.js';
import { disablePush } from '../platform/push.js';
import { Menu, MenuItem, MenuSeparator } from '../ui/Menu.js';
const ProfilePanel = lazy(() => import('../features/account/ProfilePanel.js'));
const DisplaySheet = lazy(() => import('../features/account/DisplaySettings.js'));
const HintCard = lazy(() => import('../features/hints/HintCard.js'));
import { forgetHintStates, useActiveHint } from '../features/hints/hints.js';
import { CommandPalette, openPalette } from '../features/palette/CommandPalette.js';
import { PreviewBanner } from '../features/project/Guests.js';

/** AF08: höchstens ein situativer Vorschlag; die Karte lädt erst, wenn es einen gibt */
function HintHost() {
  const hint = useActiveHint();
  return hint ? (
    <Suspense fallback={null}>
      <HintCard key={hint.id} hint={hint} />
    </Suspense>
  ) : null;
}

/** Rahmen für angemeldete Seiten. Projekte zeigen später ihren eigenen, ruhigen Kopf (Insel-Prinzip). */
export function Shell({
  children,
  island,
  actions,
  wide,
}: {
  children: ReactNode;
  /** `pending`: Projektname noch unbekannt – Kopf steht mit Ladestruktur (0.157, ADR 0163) */
  island?: { name: string; color?: string | null; id?: string; pending?: boolean };
  actions?: ReactNode;
  wide?: boolean;
}) {
  const me = useMe();
  const config = useConfig();
  const appearance = useQuery({
    queryKey: ['appearance', `/api/appearance/project/${island?.id}`],
    queryFn: () => api.get<BrandingDto>(`/api/appearance/project/${island?.id}`),
    enabled: !!island?.id,
  });
  const projectColor = appearance.data?.effective.color ?? island?.color;
  const projectLogo = appearance.data?.effective.logo;
  useEffect(() => { document.title = island ? `${island.name} · ${config.data?.instanceName ?? t('app.name')}` : config.data?.instanceName ?? t('app.name'); }, [island?.name, config.data?.instanceName]);
  const status = useConnectionStatus();
  const box = useOutboxStatus(outbox);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(false);
  const [look, setLook] = useState(false);
  // 0.151 (ADR 0153): Kontowahl der Darstellung übernehmen (bzw. eine Gerätewahl ins Konto bringen)
  useEffect(() => syncDisplay(me.data), [me.data]);
  const theme = useResolvedTheme();
  // Logo je Modus; fehlt die passende Fassung, steht die andere auf einer kontrastierenden Plakette (nie Weiß auf Weiß)
  const lightLogo = config.data?.logo ?? null,
    darkLogo = config.data?.logoDark ?? null;
  const logoSrc = theme === 'dark' ? (darkLogo ?? lightLogo) : (lightLogo ?? darkLogo);
  const logoPlate = theme === 'dark' ? !darkLogo && !!lightLogo : !lightLogo && !!darkLogo;
  useEffect(() => {
    const fav = config.data?.favicon;
    if (!fav) return;
    for (const l of document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')) {
      l.href = fav;
      l.removeAttribute('sizes');
      l.type = fav.slice(5, fav.indexOf(';'));
    }
  }, [config.data?.favicon]);
  const linkGuest = isLinkGuestEmail(me.data?.user.email);

  async function signOut(force = false) {
    // Offene Änderungen nicht stillschweigend verwerfen
    // Briefing Arbeitsalltag A0.1: auch Entwürfe, die nur im Arbeitsspeicher liegen, gehen beim Abmelden verloren
    const unsafe = hasUnsafe();
    if (!force && (box.pending > 0 || unsafe)) {
      toast(box.pending > 0 ? t('sync.signOutPending', { n: box.pending }) : t('save.signOutUnsafe'), {
        kind: 'error',
        ms: 10_000,
        action: { label: t('sync.signOutAnyway'), run: () => void signOut(true) },
      });
      return;
    }
    if (force) outbox.discardMine();
    if (me.data) clearDocDrafts(me.data.user.id);
    outbox.setUser(null);
    realtime.stop();
    // P23: Push für diesen Browser endet mit der Sitzung
    await disablePush().catch(() => null);
    await authClient.signOut();
    queryClient.clear();
    await clearPersisted();
    forgetHintStates();
    await navigate({ to: '/anmelden' });
  }

  return (
    <div className="min-h-dvh" style={accentStyle(config.data?.color)}>
      <PreviewBanner />
      <header className="sticky top-0 z-20 border-b border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
        <div className="grid min-h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-1 px-2 sm:gap-3 sm:px-4 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,28rem)_minmax(0,1fr)] lg:px-6">
          <nav aria-label={t('shell.navigation')} className="flex min-w-0 items-center gap-1 sm:gap-3">
            <Link to="/" aria-label={config.data?.instanceName ?? t('app.name')} className="mr-1 shrink-0 rounded-lg p-1 max-[360px]:hidden">
              <img src={logoSrc ?? '/icon.svg'} alt="" data-logo={logoSrc === darkLogo && darkLogo ? 'dark' : logoSrc ? 'light' : 'werft'} className={`size-8 object-contain ${logoPlate ? (theme === 'dark' ? 'rounded-md bg-white p-0.5' : 'rounded-md bg-zinc-900 p-0.5') : ''}`} />
            </Link>
            <Link
              to="/"
              activeOptions={{ exact: true }}
              activeProps={{ className: 'bg-zinc-100 font-semibold dark:bg-zinc-800' }}
              className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg px-2 text-sm whitespace-nowrap sm:px-3"
            >
              {/* Etappe E (S8): sehr schmal (≤ 360 px, große Schrift) nur Symbole – Beschriftung bleibt für Screenreader */}
              <Home className="hidden size-4 max-[360px]:block sm:block" aria-hidden /> <span className="max-[360px]:sr-only">{t('lobby.title')}</span>
            </Link>
            <Link
              to="/heute"
              activeProps={{ className: 'bg-zinc-100 font-semibold dark:bg-zinc-800' }}
              className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg px-2 text-sm whitespace-nowrap sm:px-3"
            >
              <Sun className="hidden size-4 max-[360px]:block sm:block" aria-hidden /> <span className="max-[360px]:sr-only">{t('myday.title')}</span>
            </Link>
          </nav>
          <form
            role="search"
            className="hidden lg:block"
            onSubmit={(e) => {
              e.preventDefault();
              openPalette();
            }}
          >
            <label className="flex min-h-11 items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3 text-zinc-500 focus-within:border-[var(--color-accent)] dark:border-zinc-700 dark:bg-zinc-900">
              <Search className="size-4 shrink-0" aria-hidden />
              <input
                aria-label={t('shell.search')}
                placeholder={island ? t('shell.searchProject') : t('shell.searchAll')}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                value=""
                onChange={(e) => openPalette(e.target.value)}
                onClick={() => openPalette()}
              />
              <kbd className="shrink-0 text-xs">Ctrl K</kbd>
            </label>
          </form>
          <div className="flex items-center justify-end gap-1 sm:gap-2">
            <div className="hidden items-center gap-2 border-r border-zinc-200 pr-3 xl:flex dark:border-zinc-800">
              <SyncStatus status={status} pending={box.pending} waiting={box.waiting} />
              <span aria-hidden title={status} className={`size-2 rounded-full ${status === 'online' && !box.waiting ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            </div>
            <UnsavedButton />
            <Button variant="ghost" className="lg:hidden" onClick={() => openPalette()} aria-label={t('palette.open')}>
              <Search className="size-4" aria-hidden />
            </Button>
            {me.data && <InboxButton />}
            {me.data && (
              <div className="ml-1 border-l border-zinc-200 pl-1 sm:pl-3 dark:border-zinc-800">
                <Menu
                  label={t('profile.menu')}
                  trigger={
                    <span className="grid size-9 overflow-hidden rounded-full bg-zinc-200 place-items-center text-sm font-semibold text-zinc-700 dark:bg-zinc-700 dark:text-zinc-100">
                      {me.data.user.image ? (
                        <img src={me.data.user.image} alt="" className="size-full object-cover" />
                      ) : (
                        me.data.user.name
                          .split(/\s+/)
                          .slice(0, 2)
                          .map((x) => x[0])
                          .join('')
                          .toUpperCase()
                      )}
                    </span>
                  }
                >
                  <div className="max-w-64 border-b border-zinc-200 px-3 py-3 dark:border-zinc-700">
                    <p className="truncate text-sm font-semibold">{me.data.user.name}</p>
                    <p className="truncate text-xs text-zinc-500">{linkGuest ? t('linkGuest.session') : me.data.user.email}</p>
                  </div>
                  {/* N01: Linkgäste haben kein Konto zum Verwalten und keine Arbeitsgruppen */}
                  {!linkGuest && (
                    <MenuItem onSelect={() => setTimeout(() => setProfile(true), 0)}>
                      <UserRound className="size-4" aria-hidden />
                      {t('profile.edit')}
                    </MenuItem>
                  )}
                  <MenuItem onSelect={() => setTimeout(() => setLook(true), 0)} data-menu-action="look">
                    <SunMoon className="size-4" aria-hidden />
                    {t('look.menu')}
                  </MenuItem>
                  {!linkGuest && (
                    <MenuItem onSelect={() => void navigate({ to: '/arbeitsgruppen' })}>
                      <Building2 className="size-4" aria-hidden />
                      {t('shell.workspaces')}
                    </MenuItem>
                  )}
                  {me.data.user.isInstanceAdmin && (
                    <MenuItem onSelect={() => void navigate({ to: '/verwaltung' })}>
                      <Shield className="size-4" aria-hidden />
                      {t('shell.admin')}
                    </MenuItem>
                  )}
                  <MenuSeparator />
                  <MenuItem onSelect={() => void signOut()}>
                    <LogOut className="size-4" aria-hidden />
                    {t('lobby.signOut')}
                  </MenuItem>
                </Menu>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 pb-1 text-xs xl:hidden">
          <SyncStatus status={status} pending={box.pending} waiting={box.waiting} />
        </div>
        {island && (
          <div className="flex min-h-16 items-center gap-3 border-t border-zinc-200 bg-zinc-50/80 px-4 lg:px-6 dark:border-zinc-800 dark:bg-zinc-900/50">
            <span aria-hidden className="h-8 w-1 shrink-0 rounded-full" style={{ background: projectColor ?? 'var(--color-accent)' }} />
            {projectLogo && <img src={projectLogo} alt="" className="size-9 shrink-0 rounded object-contain" />}
            {island.pending ? (
              <span aria-hidden className="block h-5 w-48 max-w-[50%] flex-1 rounded-md bg-zinc-200/70 dark:bg-zinc-800/70" />
            ) : (
              <h1 className="min-w-0 flex-1 text-base font-semibold max-sm:line-clamp-2 sm:truncate" title={island.name}>
                {island.name}
              </h1>
            )}
            <div className="flex shrink-0 items-center gap-1 border-l border-zinc-200 pl-3 dark:border-zinc-700">{actions}</div>
          </div>
        )}
      </header>
      <main style={accentStyle(projectColor)} className={`mx-auto min-w-0 px-4 py-4 lg:px-6 ${island || wide ? 'max-w-none' : 'max-w-6xl'}`}>
        {children}
      </main>
      {profile && (
        <Suspense fallback={null}>
          <ProfilePanel onClose={() => setProfile(false)} />
        </Suspense>
      )}
      {look && (
        <Suspense fallback={null}>
          <DisplaySheet signedIn={!!me.data} onClose={() => setLook(false)} />
        </Suspense>
      )}
      <Toaster />
      <HintHost />
      {me.data && <CommandPalette />}
    </div>
  );
}

/**
 * Speicherstand im Kopf: still, solange alles gespeichert und verbunden ist. Offene Änderungen erscheinen erst nach
 * kurzer Zeit (kein Flackern bei normalem Speichern); offline steht die Zahl der wartenden Änderungen da.
 */
/**
 * ADR 0175 (A09): Nicht gespeicherte Änderungen bleiben nach dem Schließen erreichbar – ein dauerhafter Hinweis im
 * Kopf führt zur betroffenen Aufgabe zurück, bis der Fehler behoben oder bewusst ausgeblendet ist.
 */
const PROBLEM_ROUTES: Record<string, [string, string]> = {
  task: ['/p/$projectId', 'aufgabe'],
  event: ['/p/$projectId/termine', 'termin'],
  contact: ['/p/$projectId/personen', 'kontakt'],
  decision: ['/p/$projectId/freigaben', 'entscheidung'],
  budget: ['/p/$projectId/budget', 'position'],
  goal: ['/p/$projectId/ziele', 'ziel'],
  risk: ['/p/$projectId/ziele', 'risiko'],
  shift: ['/p/$projectId/einsaetze', 'einsatz'],
  file: ['/p/$projectId/dateien', 'datei'],
  page: ['/p/$projectId/notizen', 'seite'],
};

function UnsavedButton() {
  const problems = useProblems();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!problems.length) return null;
  const go = (key: string, projectId: string | null) => {
    if (!projectId) return;
    const [kind, id] = key.split(':');
    setOpen(false);
    // ADR 0176: jede Inhaltsart führt zu ihrem Fenster (Schlüssel `<art>:<id>` aus saveStatus)
    const to = PROBLEM_ROUTES[kind ?? ''];
    if (to) void navigate({ to: to[0], params: { projectId }, search: { [to[1]]: id } as never });
    else void navigate({ to: '/p/$projectId', params: { projectId } });
  };
  // Nachprüfung 0.167.2, Punkt 2: auch ungesicherte Entwürfe stehen hier – mit Öffnen, Kopieren und bewusstem Verwerfen
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={t('save.unsavedList')}
      anchor={
        <Button
          variant="ghost"
          className="text-red-700 dark:text-red-400"
          data-unsaved={problems.length}
          aria-label={t('save.unsavedOpen', { n: problems.length, what: problems.flatMap((p) => p.labels).join(', ') })}
          onClick={() => setOpen(true)}
        >
          <AlertTriangle className="size-4" aria-hidden />
          <span className="text-sm">{t('save.unsaved', { n: problems.length })}</span>
        </Button>
      }
    >
      {open && (
        <ul className="space-y-2" data-unsaved-list="">
          {problems.map((p) => (
            <li key={p.key} className="space-y-1 rounded-lg border border-zinc-200 p-2 dark:border-zinc-800" data-unsaved-item={p.key}>
              <p className="font-medium">{p.labels.join(', ')}</p>
              {p.ops.some((o) => o.durable === false) && <p className="text-xs text-red-700 dark:text-red-400">{t('save.localOnlyHint')}</p>}
              <div className="flex flex-wrap gap-1">
                <Button variant="ghost" onClick={() => go(p.key, p.projectId)} data-unsaved-open="">
                  {t('save.openContent')}
                </Button>
                {p.ops
                  .filter((o) => o.copy)
                  .map((o) => (
                    <CopyButton key={o.id} op={o} />
                  ))}
                {p.ops
                  .filter((o) => o.durable === false)
                  .map((o) => (
                    <DiscardButton key={o.id} contentKey={p.key} op={o} />
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}

function SyncStatus({ status, pending, waiting }: { status: string; pending: number; waiting: boolean }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!pending) return setSlow(false);
    const timer = setTimeout(() => setSlow(true), 1500);
    return () => clearTimeout(timer);
  }, [pending > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  let text: string | null = null;
  if (pending && (waiting || status !== 'online')) text = pending === 1 ? t('sync.waitingOne') : t('sync.waiting', { n: pending });
  else if (pending && slow) text = pending === 1 ? t('sync.pendingOne') : t('sync.pending', { n: pending });
  else if (status === 'offline') text = t('common.offline');
  else if (status === 'connecting') text = t('common.reconnecting');
  return (
    <span
      role="status"
      data-testid="sync-status"
      title={status === 'offline' ? t('offline.reading') : undefined}
      className="max-w-[45vw] truncate text-xs text-amber-700 dark:text-amber-400"
    >
      {text}
    </span>
  );
}
